import numeral from 'numeral';

import {
	CURRENCY_PREFIX,
	NumberFormat,
	PERCENTAGE_SUFFIX,
} from '~/converters/number-format-constants';
import { NumberScaleUtils } from '~/utils/number-scale-utils';
import { StringUtils } from '~/utils/string-utils';

type NumberDataType =
	| 'BigDecimal'
	| 'Currency'
	| 'Integer'
	| 'Long'
	| 'Percentage';

/**
 * NumberFormatValueConverter is mainly used for display value. It is not
 * recommended to apply it on an input element which mixes a lot of behaviors,
 * e.g, validation and input mask. Input mask takes the role of formatter over
 * this converter.
 *
 * This converter utilises `NumberScaleUtils` to calculate how many digits
 * after the decimal point. It is called `scale` in the backend, SQL or API,
 * `precision` is also used for describe the number of decimal places.
 */
export class NumberFormatValueConverter {
	/**
	 * Allow to optionally declare a number data type
	 * (e.g. `converters.ts`)
	 */
	constructor(private numberDataType?: NumberDataType) {}

	/**
	 * Parameterized by dataType so it could be used by `number.html` on any type of field data type
	 */
	public toView(value, scale?, dataType?) {
		// Obey the `dataType` parameter first, then default to `this.numberDataType`
		// If both are undefined, scale simply defaults to 0 (see `toViewDefault`)
		switch (dataType || this.numberDataType) {
			case 'Percentage':
				return this.toViewPercentage(value, scale);
			case 'Currency':
				return this.toViewCurrency(value, scale);
			case 'Integer':
			case 'Long':
				return this.toViewDefault(value, 0); // Force zero scale
			default:
				return this.toViewDefault(value, scale);
		}
	}

	private calcNumeralFormat(value, scale?): string {
		const decimalPoint = NumberFormat.Decimal.Optional ? '[.]' : '.';
		const integerPart = NumberFormat.Integer.AutoGroup ? '0,0' : '0';
		const fixedDigits = NumberScaleUtils.getScaleDefault(value, scale);
		const decimalPart = '0'.repeat(fixedDigits);

		return [integerPart, decimalPoint, decimalPart].join('');
	}

	/**
	 * Default number conversion
	 * scale is optional and defaults to 0 (see `NumberScaleUtils.getScaleDefault`)
	 * If value is not a number, both value and scale are set to 0 so conversion result will be 0
	 */
	protected toViewDefault(value, scale?) {
		if (
			value === undefined ||
			value === null ||
			isNaN(value) ||
			StringUtils.isBlank(value.toString())
		) {
			value = 0;
			scale = 0;
		}
		return numeral(value).format(this.calcNumeralFormat(value, scale));
	}

	/**
	 * Simply adds a '%' sign at the end
	 */
	protected toViewPercentage(value, scale?) {
		return this.toViewDefault(value, scale) + PERCENTAGE_SUFFIX;
	}

	/**
	 * Simply adds a '$' sign at the beginning
	 * Currency scale defaults to 2 (NumberFormat.Currency.Digits)
	 */
	protected toViewCurrency(value, scale?) {
		// if value is not a correctly defined number, set value to 0, ignore any
		// provided scale so it falls back to default and view will be set to '$ 0.00'
		if (
			value === undefined ||
			value === null ||
			isNaN(value) ||
			StringUtils.isBlank(value.toString())
		) {
			value = 0;
			scale = undefined;
		}
		const defaultScale = NumberScaleUtils.getScaleCurrency(value, scale);
		return CURRENCY_PREFIX + this.toViewDefault(value, defaultScale);
	}
}
